import { Button } from "antd";
import { ReactElement, useEffect, useState } from "react";

import "./edit-banks.scss";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import toast from "react-hot-toast";
import { PlusSquareOutlined } from "@ant-design/icons";
import BankForm from "./bankForm";
import { LOADING } from "src/store/actions";
import instance from "src/store/axios";
import CustomModal from "src/components/custom-modal/custom-modal";
import DeleteModal from "src/components/delete-modal/delete-modal";

const EditBanks: React.FC<{ onSuccess: () => void }> = ({
  onSuccess,
}): ReactElement => {
  const { accountId, activeKyc } = useParams();
  const dispatch = useDispatch();
  const [standingData, setStandingData] = useState<any>();
  const [openDeleteModal, setOpenDeleteModal] = useState<any>(false);
  const [selectedBank, setSelectedBank] = useState<any>();
  const [banksArray, setBanksArray] = useState<any>([]);
  const [banksData, setBanksData] = useState<any>([]);
  const [countriesList, setCountriesList] = useState<any>([]);
  const [accountDetails, setAccountDetails] = useState<any>();

  const [currenciesList, setCurrenciesList] = useState<any>([]);

  useEffect(() => {
    async function prepareData() {
      dispatch({ type: LOADING, payload: true });

      await getCountriesList();
      await getBanksData();
      await getStandindData();
      await getKycApi();

      dispatch({ type: LOADING, payload: false });
    }

    prepareData();
  }, []);

  const getCountriesList = async () => {
    try {
      const response: AxiosResponse<{
        data: any[];
        message: string;
      }> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/countries?partial=${1}`
      );
      const countries = response.data.data;

      // Extract unique currencies from countries list
      // For USD, only use United States
      const uniqueCurrencies = countries.reduce((acc: any[], country: any) => {
        if (
          country.currencyCode &&
          !acc.some((c: any) => c.currencyCode === country.currencyCode)
        ) {
          if (country.currencyCode === "USD" && country.countryCode !== "US") {
            return acc;
          }
          acc.push(country);
        }
        return acc;
      }, []);
      setCurrenciesList(uniqueCurrencies);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
  };

  const getBanksData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/banks`
      );
      const banks = response.data.data;
      setBanksData(banks);

      // Extract unique countries from banks list
      const uniqueCountries = banks.reduce((acc: any[], bank: any) => {
        if (bank.country && !acc.some((c: any) => c.id === bank.country.id)) {
          acc.push(bank.country);
        }
        return acc;
      }, []);
      setCountriesList(uniqueCountries);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
  };

  const getKycApi = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
      );
      setAccountDetails(response.data.data);
      if (response.data.data?.bankAccounts?.length) {
        setBanksArray(response.data.data?.bankAccounts);
      } else {
        setBanksArray([{}]);
      }
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
  };

  const getStandindData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/standing-data`
      );
      setStandingData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
  };

  const onSubmit = async (values: any): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    values.kycId = accountDetails.accountInfo[0]?.id;
    values.branch = 'SA';

    try {
      if (!values?.id) {
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/bank-account`,
          values
        );
      } else {
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/bank-account/${values?.id}`,
          values
        );
      }

      toast.success("bank accounts data updated successfully");
      await getKycApi();
      onSuccess();
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
        localStorage.setItem(
          "star_prev_url",
          `/home/new-kyc/${accountDetails?.id}/${accountDetails?.pendingKycId}`
        );
      }
    }

    dispatch({ type: LOADING, payload: false });
  };

  const deleteBankAccount = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/bank-account/${selectedBank?.id}/delete`
      );
      toast.success("bank account deleted successfully");
      setOpenDeleteModal(false);
      await getKycApi();
      onSuccess();
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }

    dispatch({ type: LOADING, payload: false });
  };

  return (
    <div className="edit-banks flex flex-col">
      <h3 className=" font-semibold text-xl mb-5">Bank accounts details</h3>

      {accountDetails &&
        banksArray?.map((bank: any, index: number) => (
          <>
            <div className="flex gap-3 items-center mb-5">
              <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
              <h3 className=" text-lg font-semibold">Bank #{index + 1}</h3>
            </div>
            <BankForm
              key={bank?.id || `new-${index}`}
              bank={bank}
              index={index}
              onSubmit={onSubmit}
              countriesList={countriesList}
              banksData={banksData}
              standingData={standingData}
              currenciesList={currenciesList}
              setSelectedBank={setSelectedBank}
              setOpenDeleteModal={setOpenDeleteModal}
              accountDetails={accountDetails}
            />
          </>
        ))}
      {accountDetails && (
        <div className="add-field-button flex justify-start w-full">
          <Button
            onClick={() => {
              setBanksArray((prev: any) => [...prev, {}]);
            }}
            className=" border-0 text-blue-500 bg-transparent mb-2 shadow-none p-0 text-base"
            icon={<PlusSquareOutlined />}
          >
            Add a new bank account
          </Button>
        </div>
      )}
      <CustomModal
        isVisible={openDeleteModal}
        centered
        width={450}
        onClose={() => {
          setOpenDeleteModal(false);
        }}
        className="delete-modal"
      >
        <DeleteModal
          onCancel={() => setOpenDeleteModal(false)}
          onConfirm={deleteBankAccount}
          text="Delete bank account!"
          subText={`Are you sure you want to delete this bank account?`}
        />
      </CustomModal>
    </div>
  );
};

export default EditBanks;
